import {
  estimateSettlement,
  FEE_RATE_FREE,
  getEstimatedFeeRate,
  isServerSettlement,
} from './platformFee';

/** Fields the server recalculates — never persist or trust from client writes. */
export const CLIENT_FEE_FIELD_KEYS = [
  'platformFeeRate',
  'platformFeeAmount',
  'platformFee',
  'winnerPayoutAmount',
  'payoutAmount',
  'subscriptionDiscountApplied',
];

export function stripClientFeeFields(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const next = { ...obj };
  CLIENT_FEE_FIELD_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}

/** @deprecated use FEE_RATE_FREE from platformFee */
export const PLATFORM_FEE_RATE = FEE_RATE_FREE;

export function calculatePlatformFee(totalPot, profileOrHasPlus) {
  return estimateSettlement(totalPot, profileOrHasPlus).platformFeeAmount;
}

export function escrowPayoutAmount(totalPot, profileOrHasPlus) {
  return estimateSettlement(totalPot, profileOrHasPlus).winnerPayoutAmount;
}

export function isGameFinal(gameStatus) {
  if (!gameStatus) return false;
  const s = String(gameStatus).toUpperCase();
  return (
    s === 'FT'
    || s === 'FINAL'
    || s.includes('FINAL')
    || s === 'FULL TIME'
    || s === 'GAME OVER'
    || s === 'ENDED'
  );
}

export function computeWinningTeamFromScores(teamA, teamB, scoreA, scoreB) {
  if (scoreA == null || scoreB == null) return null;
  if (scoreA > scoreB) return teamA;
  if (scoreB > scoreA) return teamB;
  return 'DRAW';
}

export function computeWinningTeamFromMatchup(matchup) {
  if (!matchup) return null;
  return computeWinningTeamFromScores(
    matchup.teamA,
    matchup.teamB,
    matchup.scoreA,
    matchup.scoreB
  );
}

/**
 * Winner/outcome metadata for reporting — no fee fields (server calculates at settlement).
 * @param {object} bet
 * @param {string} winningTeamOrPick — team name (live) or pick label (custom)
 * @param {'game_final' | 'funds_released' | 'votes_agreed'} [kind]
 */
export function buildResolutionFromBet(bet, winningTeamOrPick, kind = 'game_final') {
  const matchup = bet?.matchup || {};
  const winningTeam =
    winningTeamOrPick
    || (matchup.isCustom ? null : computeWinningTeamFromMatchup(matchup));
  const isDraw = winningTeam === 'DRAW';
  const winner = isDraw ? null : findWinningParticipant(bet, winningTeam);
  const totalPot = Number(bet?.totalPot) || 0;

  return {
    kind,
    winningTeam: winningTeam || '—',
    winningPlayerId: winner?.id ?? null,
    winningPlayerName: winner?.name ?? (isDraw ? '—' : '—'),
    totalPot,
    description: matchup.description || 'Challenge',
    isDraw,
    isCustom: !!matchup.isCustom,
  };
}

/** UI/notification estimate — not written to Firestore or sent as settlement truth. */
export function withFeeEstimate(outcome, profileOrHasPlus) {
  if (!outcome || outcome.isDraw) return outcome;
  const estimate = estimateSettlement(outcome.totalPot, profileOrHasPlus);
  return { ...outcome, ...estimate, _estimate: true };
}

/**
 * @param {object} bet
 * @param {string} winningTeamOrPick — team name (live) or pick label (custom)
 */
export function findWinningParticipant(bet, winningTeamOrPick) {
  if (!bet?.participants?.length || !winningTeamOrPick || winningTeamOrPick === 'DRAW') {
    return null;
  }
  const byPick = bet.participants.find((p) => p.pick === winningTeamOrPick);
  if (byPick) return byPick;
  const byName = bet.participants.find(
    (p) => p.name === winningTeamOrPick || winningTeamOrPick.includes(p.name)
  );
  if (byName) return byName;
  if (winningTeamOrPick === 'Outcome A' && bet.participants[0]) return bet.participants[0];
  if (winningTeamOrPick === 'Outcome B' && bet.participants[1]) return bet.participants[1];
  return null;
}

/** Custom wager: both players voted the same outcome label. */
export function buildResolutionFromCustomVote(bet, winnerPick) {
  const winner = findWinningParticipant(bet, winnerPick)
    || bet.participants?.find((p) => p.name === winnerPick);
  const winningTeam = winner?.pick || winnerPick;
  const resolution = buildResolutionFromBet(bet, winningTeam, 'votes_agreed');
  if (winner) {
    resolution.winningPlayerId = winner.id;
    resolution.winningPlayerName = winner.name;
    resolution.winningTeam = winningTeam;
  }
  return resolution;
}

export function syncMatchupFromLiveEvent(matchup, event) {
  return mergeMatchupFromLiveGame(matchup, event);
}

/**
 * Display + patch scores — prefers live game row; blocks false 0–0 after final.
 */
export function resolveChallengeDisplayScores(matchup, liveGame) {
  const status = String(liveGame?.status ?? matchup?.gameStatus ?? '');
  const final = isGameFinal(status);

  const liveA = liveGame?.scoreA;
  const liveB = liveGame?.scoreB;
  if (liveA != null && liveB != null) {
    if (final && liveA === 0 && liveB === 0) {
      return {
        scoreA: null,
        scoreB: null,
        gameStatus: liveGame?.status ?? status,
        scoreUnavailable: true,
      };
    }
    return {
      scoreA: liveA,
      scoreB: liveB,
      gameStatus: liveGame?.status ?? status,
      scoreUnavailable: false,
    };
  }

  const snapA = matchup?.scoreA;
  const snapB = matchup?.scoreB;
  if (snapA != null && snapB != null) {
    if (final && snapA === 0 && snapB === 0) {
      return {
        scoreA: null,
        scoreB: null,
        gameStatus: status,
        scoreUnavailable: true,
      };
    }
    return {
      scoreA: snapA,
      scoreB: snapB,
      gameStatus: status,
      scoreUnavailable: false,
    };
  }

  if (final) {
    return {
      scoreA: null,
      scoreB: null,
      gameStatus: status,
      scoreUnavailable: true,
    };
  }

  return {
    scoreA: null,
    scoreB: null,
    gameStatus: status,
    scoreUnavailable: false,
  };
}

/** Merge live /games row onto challenge matchup for display or Firestore patch. */
export function mergeMatchupFromLiveGame(matchup, liveGame) {
  if (!liveGame) return {};
  const display = resolveChallengeDisplayScores(matchup, liveGame);
  const patch = {
    gameStatus: display.gameStatus ?? liveGame.status ?? matchup?.gameStatus,
    scoreUnavailable: display.scoreUnavailable,
  };
  if (!display.scoreUnavailable && display.scoreA != null && display.scoreB != null) {
    patch.scoreA = display.scoreA;
    patch.scoreB = display.scoreB;
  }
  return patch;
}

export function findLiveEventForMatchup(events, matchup) {
  if (!Array.isArray(events) || !matchup) return null;
  if (matchup.gameId) {
    const byId = events.find((e) => e.id === matchup.gameId);
    if (byId) return byId;
  }
  const teamA = (matchup.teamA || '').trim().toLowerCase();
  const teamB = (matchup.teamB || '').trim().toLowerCase();
  if (!teamA || !teamB) return null;
  return (
    events.find((e) => {
      const a = (e.a || '').trim().toLowerCase();
      const b = (e.b || '').trim().toLowerCase();
      return (a === teamA && b === teamB) || (a === teamB && b === teamA);
    }) || null
  );
}

/** True once both sides are in escrow (no opt-out / early release). */
export function isEscrowLockedStatus(status) {
  return (
    status === 'ACTIVE'
    || status === 'LOCKED_IN'
    || status === 'PENDING_WINNER'
    || status === 'DISPUTED'
  );
}

export function isWinnerDetermined(bet) {
  return Boolean(bet?.finalOutcome?.winningPlayerId);
}

export function isUserWinner(bet, userId) {
  return Boolean(userId && bet?.finalOutcome?.winningPlayerId === userId);
}

function displayPayoutAmount(resolution) {
  if (!resolution) return 0;
  if (resolution.winnerPayoutAmount != null) return resolution.winnerPayoutAmount;
  if (resolution.payoutAmount != null) return resolution.payoutAmount;
  if (resolution.totalPot) {
    return estimateSettlement(resolution.totalPot, null).winnerPayoutAmount;
  }
  return 0;
}

function formatPayoutSummary(resolution) {
  const fromServer = isServerSettlement(resolution);
  const breakdown = fromServer
    ? resolution
    : estimateSettlement(resolution.totalPot || 0, null);
  const feeRate = breakdown.platformFeeRate ?? getEstimatedFeeRate(false);
  const feeAmt = breakdown.platformFeeAmount ?? breakdown.platformFee ?? 0;
  const payout = breakdown.winnerPayoutAmount ?? breakdown.payoutAmount ?? 0;
  const pct = Math.round(feeRate * 100);
  const feeLabel = breakdown.subscriptionDiscountApplied ? `LockedIn Plus ${pct}%` : `Platform ${pct}%`;
  const prefix = fromServer ? '' : 'Est. ';
  return (
    `${prefix}Challenge pool $${Number(breakdown.totalPot || 0).toFixed(2)} · `
    + `${fromServer ? feeLabel : `fee 5% or 2% Plus (winner at settlement)`} · `
    + `${prefix}Winner receives $${Number(payout).toFixed(2)}`
    + (fromServer ? ` · Fee $${Number(feeAmt).toFixed(2)}` : '')
  );
}

export function formatWinnerNotificationMessage(
  resolution,
  { released = false, autoReleased = false } = {}
) {
  if (!resolution) return '';
  if (resolution.isDraw) {
    return `Game ended in a tie: ${resolution.description}. Locked funds remain until you agree on next steps.`;
  }
  const action = autoReleased
    ? 'added to your wallet'
    : released
      ? 'released to'
      : 'will be released to';
  const header = autoReleased
    ? 'You won — earnings added to wallet'
    : released
      ? 'Challenge settled'
      : 'Challenge complete';
  const payee = autoReleased ? 'you' : resolution.winningPlayerName;
  const payout = displayPayoutAmount(resolution);
  return (
    `${header} — ${resolution.description}\n`
    + `Winning team: ${resolution.winningTeam}\n`
    + `Winner: ${resolution.winningPlayerName}\n`
    + `$${payout.toFixed(2)} ${action}${autoReleased ? '' : ` ${payee}`}.\n`
    + formatPayoutSummary(resolution)
  );
}

/** Live game loss — opponent won; escrow auto-paid to them. */
export function formatLoserLiveNotificationMessage(resolution) {
  if (!resolution) return '';
  if (resolution.isDraw) {
    return `Game ended in a tie: ${resolution.description}. Locked funds remain until you agree on next steps.`;
  }
  return (
    `📉 Game final — you lost\n`
    + `${resolution.description}\n`
    + `Winner: ${resolution.winningPlayerName} (${resolution.winningTeam})\n`
    + `$${displayPayoutAmount(resolution).toFixed(2)} was added to their wallet.\n`
    + formatPayoutSummary(resolution)
  );
}
